import { useMsal } from '@azure/msal-react';
import InfoIcon from '@mui/icons-material/Info';
import { Alert, AlertTitle, Box, CircularProgress, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material';
import { AbsReactTable, LoadingMessage, TABLE } from 'customer_portal-ui-shared';
import React, { useEffect, useState, useRef } from 'react';
import { PSCChecklist, VesselDestination, VesselETA } from '../component';
import { AisService, MouService, VesselService, getAuthToken } from '../service';
import lookupsUtils from '../utils/lookups.utils';

const refVesselList = React.createRef();
const refTable = React.createRef();

// Inspection Window Month
const InspectionMonth = ({ mou, index }) => {
    const riskStyle = {
        color: 'black',
        border: '2px rgb(0, 42, 78) solid',
        width: 25,
        height: 25,
        borderRadius: '50%'
    };

    const getRiskStyle = (inspectionWindowMonth) => {
        if (inspectionWindowMonth.RiskId === lookupsUtils.PortStateControlRisk.High.Id) {
            return { ...riskStyle, backgroundColor: lookupsUtils.PortStateControlRisk.High.Color };
        }

        if (inspectionWindowMonth.RiskId === lookupsUtils.PortStateControlRisk.Medium.Id) {
            return { ...riskStyle, backgroundColor: lookupsUtils.PortStateControlRisk.Medium.Color };
        }

        if (inspectionWindowMonth.RiskId === lookupsUtils.PortStateControlRisk.Low.Id) {
            return { ...riskStyle, backgroundColor: lookupsUtils.PortStateControlRisk.Low.Color };
        }
    };

    const getTootlip = (riskId, dateToMediumRisk, dateToHighRisk) => {
        let riskLevel = '';

        if (riskId === lookupsUtils.PortStateControlRisk.High.Id) {
            riskLevel = 'High';
        }
        if (riskId === lookupsUtils.PortStateControlRisk.Medium.Id) {
            riskLevel = 'Medium';
        }
        if (riskId === lookupsUtils.PortStateControlRisk.Low.Id) {
            riskLevel = 'Low';
        }

        return (
            <Alert severity="info">
                <AlertTitle>
                    Inspection Risk is <strong>{riskLevel}</strong>
                </AlertTitle>

                {dateToMediumRisk !== dateToHighRisk && (
                    <>
                        {/* Do Not Show 'default' date. */}
                        {dateToMediumRisk !== '0001-Jan-01' && (
                            <>
                                <span>
                                    Date to Medium Risk is <strong>{dateToMediumRisk}</strong>.
                                </span>
                                <br />
                            </>
                        )}

                        {/* Do Not Show 'default' date. */}
                        {dateToHighRisk !== '0001-Jan-01' && (
                            <>
                                <span>
                                    Date to High Risk is <strong>{dateToHighRisk}</strong>.
                                </span>
                                <br />
                            </>
                        )}
                    </>
                )}
            </Alert>
        );
    };

    return (
        <>
            {mou ? (
                <Stack direction={'row'} gap={1} justifyContent={'center'}>
                    <Tooltip title={getTootlip(mou.InspectionWindow[index].RiskId, mou.DateToMediumRisk, mou.DateToHighRisk)} placement="top" arrow>
                        <Box component="span" style={getRiskStyle(mou.InspectionWindow[index])} />
                    </Tooltip>
                </Stack>
            ) : (
                <></>
            )}
        </>
    );
};

// Determine if the MOU Inspection Risk Factor (High, Medium, Low)
const MOURiskComponent = ({ mou }) => {
    return (
        <>
            {mou ? (
                <>
                    {mou.RiskId ? (
                        <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                            {mou.RiskId == lookupsUtils.PortStateControlRisk.Low.Id && <Typography style={{ color: lookupsUtils.PortStateControlRisk.Low.Color, fontWeight: 'bold' }}> Low</Typography>}
                            {mou.RiskId == lookupsUtils.PortStateControlRisk.Medium.Id && (
                                <Typography style={{ color: lookupsUtils.PortStateControlRisk.Medium.Color, fontWeight: 'bold' }}> Medium</Typography>
                            )}
                            {mou.RiskId == lookupsUtils.PortStateControlRisk.High.Id && (
                                <Typography style={{ color: lookupsUtils.PortStateControlRisk.High.Color, fontWeight: 'bold' }}> High</Typography>
                            )}
                        </Stack>
                    ) : (
                        <>Not Yet Visited</>
                    )}
                </>
            ) : (
                <></>
            )}
        </>
    );
};

// MOU locations
const MOULocation = ({ mouData, selectedMOUData, tableRowIndex, onMOUChange }) => {
    const [selectedMOU, setSelectedMOU] = useState();

    useEffect(() => {
        if (selectedMOUData) {
            setSelectedMOU(selectedMOUData.MOU);
            return;
        }

        setSelectedMOU('');
    }, [selectedMOUData]);

    useEffect(() => {
        onMOUChange(
            mouData.find((element) => element.MOU === selectedMOU),
            tableRowIndex
        );
    }, [selectedMOU]);

    return (
        <>
            {mouData <= 0 ? (
                <>No Data</>
            ) : (
                <>
                    {selectedMOU != undefined && (
                        <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                            <Select sx={{ width: 150, height: 50 }} displayEmpty value={selectedMOU} onChange={(event) => setSelectedMOU(event.target.value)}>
                                <MenuItem key={-1} value="">
                                    {`-- Select --`}
                                </MenuItem>
                                {mouData.map((item, index) => (
                                    <MenuItem key={index} value={item.MOU}>
                                        {item.MOU}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                    )}
                </>
            )}
        </>
    );
};

const CenterIMOClassNumber = (inputData) => {
    return <center>{inputData}</center>;
};

const VesselNameAndVesselType = (inputData) => {
    const data = inputData.split('-');
    return (
        <>
            {data[0]}
            <Typography variant="caption" display="block">
                {data[1]}
            </Typography>
        </>
    );
};

const PortStateControl = () => {
    const { instance, accounts } = useMsal();

    const [isLoading, setIsLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [vesselList, setVesselList] = useState([]);

    const [vesselListFiltered, setVesselListFiltered] = useState([]);
    const [vesselListFilteredToggle, setVesselListFilteredToggle] = useState(true);

    const [gridLeft, setGridLeft] = useState();
    const [gridRight, setGridRight] = useState();
    const [inspectionTabLength, setInspectionTabLength] = useState(0);

    const month1Ref = useRef(null);
    const month2Ref = useRef(null);
    const month3Ref = useRef(null);
    const PSCChecklistRef = useRef(null);

    const getInspectionRiskDate = () => {
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();

        const parseDate = (inputMonth, inputYear) => {
            const inspectionDate = new Date(inputYear, inputMonth);
            const formattedDate = inspectionDate.toLocaleString('en-US', { month: 'short' });
            return `${inputYear}-${formattedDate}`;
        };

        return [parseDate(currentMonth, currentYear), parseDate((currentMonth + 1) % 12, currentYear), parseDate((currentMonth + 2) % 12, currentYear)];
    };

    const [headerDate] = useState(getInspectionRiskDate);

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const tablePage = [10, 20, 50, 100];

    const initialFilterState = {
        searchType: 'list',
        resetPageIndex: false,
        resetFilters: true,
        filters: {},
        pageSize: '10',
        pageIndex: '0'
    };

    const [appliedFilters, setAppliedFilters] = useState({
        ...initialFilterState
    });

    const getToolTipInspectionWindow = (month) => {
        return (
            <>
                <Tooltip
                    title={
                        <Alert severity="info">
                            <AlertTitle>Inspection Risk</AlertTitle>
                            <div>
                                <span>
                                    <strong>Blue</strong> = Non Priority Vessel (Low Risk).
                                </span>
                            </div>
                            <div>
                                <span>
                                    <strong>Yellow</strong> = Vessel may be inspected (Medium Risk).
                                </span>
                            </div>
                            <div>
                                <span>
                                    <strong>Red</strong> = Vessel must be inspected (High Risk).
                                </span>
                            </div>
                        </Alert>
                    }
                    placement="left"
                    arrow
                >
                    <span>
                        {month} &nbsp;
                        <InfoIcon />
                    </span>
                </Tooltip>
            </>
        );
    };

    const [tableColumns, setTableColumns] = useState([
        {
            label: 'Vessel Name',
            accessor: 'VesselName_Format',
            Cell: ({ cell: { value } }) => VesselNameAndVesselType(value),
            sorting: false,
            filtering: false
        },
        {
            label: <center>IMO Number</center>,
            accessor: 'ImoNumber_Format',
            Cell: ({ cell: { value } }) => CenterIMOClassNumber(value),
            sorting: false,
            filtering: false
        },
        {
            label: <center>Class Number</center>,
            accessor: 'ClassNumber_Format',
            Cell: ({ cell: { value } }) => CenterIMOClassNumber(value),
            sorting: false,
            filtering: false
        },
        {
            label: 'AIS ETA',
            accessor: 'Eta',
            dataType: 'date',
            sorting: false,
            filtering: false
        },
        {
            label: (
                <Tooltip
                    title={
                        <Alert severity="info">
                            <AlertTitle>AIS Disclaimer</AlertTitle>
                            <span>
                                The first line is the destination information as entered by the vessel’s crew into the AIS to be reported. <br />
                                The second line is the Associated Destination as determined by the Regulatory World Map.
                            </span>
                        </Alert>
                    }
                    placement="left"
                    arrow
                >
                    <span>
                        AIS Destination Port &nbsp;
                        <InfoIcon />
                    </span>
                </Tooltip>
            ),
            accessor: 'AisDestination_Format',
            sorting: false,
            filtering: false
        },
        {
            label: 'MOU',
            accessor: 'MOU_Status',
            sorting: false,
            filtering: false
        },
        {
            label: (
                <Tooltip
                    title={
                        <Alert severity="info">
                            <AlertTitle>Vessel Risk</AlertTitle>
                            <span>Risk is determined by PSC Regime.</span>
                        </Alert>
                    }
                    placement="left"
                    arrow
                >
                    <span>
                        Vessel Risk &nbsp;
                        <InfoIcon />
                    </span>
                </Tooltip>
            ),
            accessor: 'InspectionRisk_Format',
            sorting: false,
            filtering: false
        },
        {
            label: (
                <>
                    <Stack ref={month1Ref}>
                        <div>{getToolTipInspectionWindow(headerDate[0])}</div>
                    </Stack>
                </>
            ),
            accessor: 'Month1_Format',
            sorting: false,
            filtering: false
        },
        {
            label: (
                <>
                    <Stack ref={month2Ref}>
                        <div>{getToolTipInspectionWindow(headerDate[1])}</div>
                    </Stack>
                </>
            ),
            accessor: 'Month2_Format',
            sorting: false,
            filtering: false
        },
        {
            label: (
                <>
                    <Stack ref={month3Ref}>
                        <div>{getToolTipInspectionWindow(headerDate[2])}</div>
                    </Stack>
                </>
            ),
            accessor: 'Month3_Format',
            sorting: false,
            filtering: false
        },
        {
            label: (
                <>
                    <Stack ref={PSCChecklistRef}>
                        <div>{'PSC Checklist'}</div>
                    </Stack>
                </>
            ),
            accessor: 'Checklist_Format',
            sorting: false,
            filtering: false
        }
    ]);

    useEffect(() => {
        document.title = 'ABS Port State Control';
        (async () => {
            await getVessel();
        })();
    }, []);

    useEffect(() => {
        (async () => {
            if (!vesselListFiltered || vesselListFiltered.length <= 0) return;

            setTableLoading(true);
            const temp = await populateVesselListInfo(vesselListFiltered);
            setVesselListFiltered(temp);
            setTableLoading(false);
        })();
    }, [vesselListFilteredToggle]);

    const handleResize = () => {
        if (PSCChecklistRef && month1Ref.current && month3Ref.current) {
            const month1REF = month1Ref.current.getBoundingClientRect();
            const month3REF = month3Ref.current.getBoundingClientRect();
            setInspectionTabLength(month3REF.right - month1REF.left);

            if (month1REF.left > 1000 && month3REF.left > 1000) {
                setGridLeft(month1REF.left);
                setGridRight(month3Ref.right);
            }
        }
    };

    useEffect(() => {
        handleResize();
    }, [vesselListFiltered, windowWidth]);

    const handleWindowResize = () => {
        setWindowWidth(window.innerWidth);
    };

    useEffect(() => {
        window.addEventListener('resize', handleWindowResize);
    }, []);

    const getVessel = async () => {
        setIsLoading(true);
        const authToken = await getAuthToken(instance, accounts);
        const result = await VesselService.getAll(authToken);

        if (!result) {
            return;
        }

        const vesselData = result.map((item) => {
            item.VesselName_Format = `${item.VesselName} - ${item.VesselType}`;
            item.ImoNumber_Format = item.ImoNumber;
            item.ClassNumber_Format = item.ClassNumber;
            item.AisDestinationPort_Format = <LoadingMessage />;
            item.Eta = <LoadingMessage />;
            item.MOU_Status = <LoadingMessage />;
            item.InspectionRisk_Format = <LoadingMessage />;
            item.Month1_Format = <LoadingMessage />;
            item.Month2_Format = <LoadingMessage />;
            item.Month3_Format = <LoadingMessage />;
            item.PSC_Checklist_Status = <LoadingMessage />;

            return item;
        });

        setVesselList(vesselData);
        setVesselListFiltered(vesselData.slice(0 * tablePage[0], tablePage[0] * (0 + 1)));
        setIsLoading(false);
    };

    // Handle event for MOU Dropdown Location  Change
    const handleSelectedMOU = (mou, mouIndex) => {
        if (!mou && mouIndex === undefined) return;

        const temp = vesselListFiltered.map((item, index) => {
            if (index === mouIndex) {
                item.InspectionRisk_Format = <MOURiskComponent mou={mou} />;
                item.Month1_Format = <InspectionMonth mou={mou} index={0} infTable={refTable} />;
                item.Month2_Format = <InspectionMonth mou={mou} index={1} infTable={refTable} />;
                item.Month3_Format = <InspectionMonth mou={mou} index={2} infTable={refTable} />;
            }

            return item;
        });

        setVesselListFiltered(temp);
    };

    // Handle event for AIS Destination MOUs
    const handleDestinationMOU = async (imoNumber, mous, mouIndex) => {
        if (!imoNumber || mouIndex === undefined || mous === undefined) return;

        const authToken = await getAuthToken(instance, accounts);
        const mouData = await MouService.getMOURisk(authToken, imoNumber);

        const selectedMOUData = mouData.find((f) => f.MOU === mous[0]);

        const temp = vesselListFiltered.map((item, index) => {
            if (index === mouIndex) {
                item.MOU_Status = <MOULocation mouData={mouData} selectedMOUData={selectedMOUData} tableRowIndex={index} onMOUChange={handleSelectedMOU} />;
                item.MOU_Status_test = { mouData, selectedMOUData, index, handleSelectedMOU };
                item.InspectionRisk_Format = <MOURiskComponent mou={mous[0]} />;
                item.Month1_Format = <InspectionMonth mou={selectedMOUData} index={0} />;
                item.Month2_Format = <InspectionMonth mou={selectedMOUData} index={1} />;
                item.Month3_Format = <InspectionMonth mou={selectedMOUData} index={2} />;
            }

            return item;
        });
        setVesselListFiltered(temp);
    };

    const populateVesselListInfo = async (tempVesselList) => {
        if (!tempVesselList || tempVesselList.length <= 0) return tempVesselList;

        const authToken = await getAuthToken(instance, accounts);

        // This is a non-blocking promise all implementation.
        // To ensure UI is rendered asynchronously.
        const temp = Promise.all(
            tempVesselList.map(async (item, index) => {
                // Default
                item.Eta = 'Not Available';
                item.AisDestination_Format = 'Not Available';
                item.MOU_Status = 'No Data';
                item.InspectionRisk_Format = '';
                item.Checklist_Format = <PSCChecklist />;

                // Return when there is no IMO Number
                if (!item.ImoNumber) return item;

                // Get AIS and MOU Data by IMO Number
                const aisData = await AisService.vesselLocation(authToken, item.ImoNumber);
                const mouData = await MouService.getMOURisk(authToken, item.ImoNumber);

                item.Eta = <VesselETA aisData={aisData} />;
                item.mouData = mouData;
                item.AisDestination_Format = <VesselDestination aisData={aisData} imoNumber={item.ImoNumber} tableRowIndex={index} onMOU={handleDestinationMOU} />;

                return item;
            })
        );

        return temp;
    };

    const searchTickets = (filters, searchType, pageIndex, pageSize) => {
        let searchData = [];
        const { searchTerm, columnId } = Object.values(filters)?.[0] || {};

        if (searchType === 'list') {
            searchData = vesselList?.slice(pageIndex * pageSize, pageSize * (pageIndex + 1));
        } else if (searchType === 'all') {
            const tempData = vesselList?.filter((ticket) => ticket[columnId].includes(searchTerm));
            searchData = tempData.slice(pageIndex * pageSize, pageSize * (pageIndex + 1));
        }

        // sort data based on the vessel name a -to-z order and update the page items
        setVesselListFiltered(searchData.sort((a, b) => a.VesselName.localeCompare(b.VesselName)));
        setVesselListFilteredToggle(!vesselListFilteredToggle);
    };

    const applyFilter = (filtersObj) => {
        const { pageIndex, pageSize, searchType, filters = {} } = filtersObj;
        const updatedFilters = {
            ...filtersObj
        };
        if (
            appliedFilters.searchType !== searchType ||
            appliedFilters.searchType === 'all' ||
            appliedFilters.pageSize !== pageSize ||
            appliedFilters.pageIndex !== pageIndex ||
            appliedFilters.resetFilters
        )
            searchTickets(filters, searchType, pageIndex, pageSize);

        setAppliedFilters(updatedFilters);
    };

    return (
        <div className="wrapper-content">
            {isLoading && (
                <Box
                    sx={{
                        textAlign: 'center',
                        marginTop: '132px',
                        minHeight: '700px'
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            {!isLoading && (
                <>
                    <Stack direction={'column'} spacing={5} ref={refVesselList}>
                        {gridLeft && (
                            <Box
                                sx={{
                                    backgroundColor: '#002A4E',
                                    color: 'white',
                                    padding: '20px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: inspectionTabLength,
                                    height: 10,
                                    borderTopLeftRadius: 10,
                                    borderTopRightRadius: 10,
                                    position: 'absolute',
                                    left: gridLeft,
                                    right: gridRight
                                }}
                            >
                                <Typography variant="h6" align="center" style={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                                    Inspection Risk
                                </Typography>
                            </Box>
                        )}
                        <AbsReactTable
                            loading={tableLoading}
                            tableType={TABLE.TYPE.DATA}
                            columns={tableColumns}
                            data={vesselListFiltered}
                            dataCount={vesselList.length}
                            pagination={true}
                            small={true}
                            allowColumnsShowHide={false}
                            applyFilterCallback={applyFilter}
                            appliedFilters={appliedFilters}
                            pageOptions={tablePage}
                        />
                    </Stack>
                </>
            )}
        </div>
    );
};

export default PortStateControl;
